import {assert} from 'Asserts';
import {ParameterStructuredTableMode} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import {
  AnalysisInputs,
  AnalysisSourcesForConfigOrExecution,
  DatasetType,
  isLabAutoExecutionDatasetInfo,
  PlateDatasetType,
  PlateDesignSpec,
  PlateRecordSpec,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import {ProcedureParameterType} from 'procedures/ProcedureDomainTypes';
import {SelectedSources} from 'procedures/shared-components/analysis/ProcedureMethodAnalysisSourceTable';
import removeNulls from 'util/removeNulls';
import {assertNever} from 'util/Types';

export type ParameterDatasetType =
  | DatasetType.DATA_PARAMETER
  | DatasetType.MATERIAL_INPUT
  | DatasetType.MATERIAL_OUTPUT
  | DatasetType.EQUIPMENT;

export const STATIC_DATASET_NAMES: Partial<Record<DatasetType, string>> = {
  // Keep names in sync with benchling/procedures/integrations/analysis/procedure_analysis_definitions.py
  [DatasetType.CONDITIONS]: 'Planned conditions',
  [DatasetType.DATA_PARAMETER]: 'Parameter values',
  [DatasetType.MATERIAL_INPUT]: 'Material input values',
  [DatasetType.MATERIAL_OUTPUT]: 'Material output values',
  [DatasetType.EQUIPMENT]: 'Equipment values',
  [DatasetType.TEST_ORDERS]: 'Test orders',
  [DatasetType.PREVIOUS_TEST_ORDERS]: 'Previous test orders',
};

export function getParameterTypeFromDatasetType(datasetType: ParameterDatasetType): ProcedureParameterType {
  switch (datasetType) {
    case DatasetType.DATA_PARAMETER:
      return ProcedureParameterType.DATA_PARAMETER;
    case DatasetType.MATERIAL_INPUT:
      return ProcedureParameterType.MATERIAL_INPUT;
    case DatasetType.MATERIAL_OUTPUT:
      return ProcedureParameterType.MATERIAL_OUTPUT;
    case DatasetType.EQUIPMENT:
      return ProcedureParameterType.EQUIPMENT;
    default:
      assert(false, `Unexpected dataset type ${assertNever(datasetType)}`);
  }
}

export function getParameterDatasetTypeFromParameterTable(
  parameterStructuredTableMode: ParameterStructuredTableMode
): DatasetType {
  switch (parameterStructuredTableMode) {
    case ParameterStructuredTableMode.PARAMETER_CONFIRMATION:
      return DatasetType.DATA_PARAMETER;
    case ParameterStructuredTableMode.PARAMETER_ACTUAL:
      return DatasetType.DATA_PARAMETER;
    case ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION:
      return DatasetType.MATERIAL_INPUT;
    case ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION:
      return DatasetType.MATERIAL_OUTPUT;
    case ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION:
      return DatasetType.EQUIPMENT;
    default:
      assert(
        false,
        `Unexpected parameter structured table mode: ${assertNever(parameterStructuredTableMode)}`
      );
  }
}

// This needs to be kept in sync with all id-based keys of
// client/procedures/shared-components/analysis/ProcedureMethodAnalysisSourceTable:SelectedSources
export const ID_BASED_DATASET_TYPES = [
  DatasetType.ENTITY_SCHEMA,
  DatasetType.INDIVIDUAL_PLATE_POSITIONS,
  DatasetType.INDIVIDUAL_PLATE_CONTENTS,
  DatasetType.LAB_AUTO,
  DatasetType.RESULT_SCHEMA,
  DatasetType.PLATE_SUMMARY,
  DatasetType.PLATE_RECORD_POSITIONS,
  DatasetType.PLATE_RECORD_CONTENTS,
  DatasetType.PLATE_DESIGN_POSITIONS,
  DatasetType.PLATE_DESIGN_CONTENTS,
];

export type IdBasedDatasetType =
  | DatasetType.ENTITY_SCHEMA
  | DatasetType.INDIVIDUAL_PLATE_POSITIONS
  | DatasetType.INDIVIDUAL_PLATE_CONTENTS
  | DatasetType.LAB_AUTO
  | DatasetType.RESULT_SCHEMA
  | DatasetType.PLATE_SUMMARY
  | DatasetType.PLATE_RECORD_POSITIONS
  | DatasetType.PLATE_RECORD_CONTENTS
  | DatasetType.PLATE_DESIGN_POSITIONS
  | DatasetType.PLATE_DESIGN_CONTENTS;

export const DATASET_TYPE_TO_SOURCES_KEY: Record<DatasetType, keyof SelectedSources> = {
  [DatasetType.CONDITIONS]: 'conditions',
  [DatasetType.DATA_PARAMETER]: 'dataParameters',
  [DatasetType.MATERIAL_INPUT]: 'materialInputs',
  [DatasetType.MATERIAL_OUTPUT]: 'materialOutputs',
  [DatasetType.EQUIPMENT]: 'equipment',
  [DatasetType.TEST_ORDERS]: 'testOrders',
  [DatasetType.PREVIOUS_TEST_ORDERS]: 'previousTestOrders',
  [DatasetType.PLATE_SUMMARY]: 'plateSchemaIds',
  [DatasetType.ENTITY_SCHEMA]: 'entitySchemaIds',
  [DatasetType.INDIVIDUAL_PLATE_POSITIONS]: 'positionsPlateSourceIds',
  [DatasetType.INDIVIDUAL_PLATE_CONTENTS]: 'contentsPlateSourceIds',
  [DatasetType.LAB_AUTO]: 'labAutoDatasetIds',
  [DatasetType.RESULT_SCHEMA]: 'resultSchemaIds',
  [DatasetType.PLATE_RECORD_POSITIONS]: 'positionsPlateRecordIds',
  [DatasetType.PLATE_RECORD_CONTENTS]: 'contentsPlateRecordIds',
  [DatasetType.PLATE_DESIGN_POSITIONS]: 'positionsPlateDesignIds',
  [DatasetType.PLATE_DESIGN_CONTENTS]: 'contentsPlateDesignIds',
};

const SOURCES_KEY_TO_DATASET_TYPE = Object.fromEntries(
  Object.entries(DATASET_TYPE_TO_SOURCES_KEY).map(([k, v]) => [v, k])
) as Record<keyof SelectedSources, DatasetType>;

function getSelectedIdsForDatasetType(datasetType: DatasetType, ss: SelectedSources): Array<string | null> {
  if (ID_BASED_DATASET_TYPES.includes(datasetType)) {
    const ids = ss[DATASET_TYPE_TO_SOURCES_KEY[datasetType]];
    assert(Array.isArray(ids), 'Expected id typed datasets to map to selected sources ids');
    return ids;
  }
  return [ss[DATASET_TYPE_TO_SOURCES_KEY[datasetType]] ? datasetType : null];
}

export function getSelectedIdsFromSelectedSources(ss: SelectedSources): Array<string> {
  return removeNulls(
    Object.keys(DatasetType).flatMap((datasetType: DatasetType) =>
      getSelectedIdsForDatasetType(datasetType, ss)
    )
  );
}

export function extractSingleSource(selectedSources: SelectedSources): string | null {
  const idsOfSelectedSources = getSelectedIdsFromSelectedSources(selectedSources);
  return idsOfSelectedSources.length === 1 ? idsOfSelectedSources[0] : null;
}

function isSourceInSublist(
  targetId: string | null,
  sources: AnalysisSourcesForConfigOrExecution | null,
  getSourceIds: (analysisInputs: AnalysisInputs) => Array<string>
): boolean {
  if (sources == null || targetId == null) {
    return false;
  }
  return sources.sourcesPerStep
    .flatMap((stepSources) => getSourceIds(stepSources.analysisInputs))
    .includes(targetId);
}

// Converts a single source ID into a fully-configured SelectedSources object. We need the full list of available sources
// in order to know the type of the single source (since that single source will appear somewhere in the full list)
//
export function convertSingleSourceIntoSelectedSources(
  singleSource: string | null,
  allAvailableSources: AnalysisSourcesForConfigOrExecution | null
): SelectedSources {
  const buildSourceIds = (getSourceIds: (analysisInputs: AnalysisInputs) => Array<string>): Array<string> =>
    isSourceInSublist(singleSource, allAvailableSources, getSourceIds) ? [singleSource!] : [];

  return {
    conditions: singleSource === SOURCES_KEY_TO_DATASET_TYPE.conditions,
    dataParameters: singleSource === SOURCES_KEY_TO_DATASET_TYPE.dataParameters,
    materialInputs: singleSource === SOURCES_KEY_TO_DATASET_TYPE.materialInputs,
    materialOutputs: singleSource === SOURCES_KEY_TO_DATASET_TYPE.materialOutputs,
    equipment: singleSource === SOURCES_KEY_TO_DATASET_TYPE.equipment,
    testOrders: singleSource === SOURCES_KEY_TO_DATASET_TYPE.testOrders,
    previousTestOrders: singleSource === SOURCES_KEY_TO_DATASET_TYPE.previousTestOrders,
    plateSchemaIds: buildSourceIds((analysisInputs) =>
      analysisInputs.plates.map((plate) => plate.plateSchemaId)
    ),
    resultSchemaIds: buildSourceIds((analysisInputs) =>
      analysisInputs.resultTables.map((table) => table.schemaId)
    ),
    entitySchemaIds: buildSourceIds((analysisInputs) =>
      analysisInputs.registrationTables.map((table) => table.schemaId)
    ),
    labAutoDatasetIds: buildSourceIds((analysisInputs) =>
      analysisInputs.laRuns.flatMap((run) =>
        run.outputs.map((output) =>
          isLabAutoExecutionDatasetInfo(output) ? output.datasetId : output.outputFileConfigId
        )
      )
    ),
    positionsPlateSourceIds: buildSourceIds((analysisInputs) =>
      analysisInputs.plates.map(({plateId}) => getPlateSourceIdentifier(plateId, PlateDatasetType.POSITIONS))
    ),
    contentsPlateSourceIds: buildSourceIds((analysisInputs) =>
      analysisInputs.plates.map(({plateId}) => getPlateSourceIdentifier(plateId, PlateDatasetType.CONTENTS))
    ),
    positionsPlateRecordIds: buildSourceIds((analysisInputs) =>
      analysisInputs.plateRecords.map((plateRecord) =>
        getPlateRecordSourceIdentifier(plateRecord, PlateDatasetType.POSITIONS)
      )
    ),
    contentsPlateRecordIds: buildSourceIds((analysisInputs) =>
      analysisInputs.plateRecords.map((plateRecord) =>
        getPlateRecordSourceIdentifier(plateRecord, PlateDatasetType.CONTENTS)
      )
    ),
    positionsPlateDesignIds: buildSourceIds((analysisInputs) =>
      analysisInputs.plateDesigns.map((plateDesign) =>
        getPlateDesignSourceIdentifier(plateDesign, PlateDatasetType.POSITIONS)
      )
    ),
    contentsPlateDesignIds: buildSourceIds((analysisInputs) =>
      analysisInputs.plateDesigns.map((plateDesign) =>
        getPlateDesignSourceIdentifier(plateDesign, PlateDatasetType.CONTENTS)
      )
    ),
  };
}

export function getIndividualPlateDatasetName(baseName: string, datasetType: PlateDatasetType): string {
  return `${baseName} ${datasetType === PlateDatasetType.POSITIONS ? 'positions' : 'contents'}`;
}

// Keep these plate source helpers in sync with benchling/procedures/integrations/analysis/seed_procedure_datasets_helpers.py
export function getPlateSummaryDatasetName(schemaName: string): string {
  return `Plates summary: ${schemaName}`;
}

/*
 * Generate a unique source identifier for a plate details dataset.
 *
 * Must be kept in sync with parsePlateSourceIdentifier.
 */
export function getPlateSourceIdentifier(plateId: string, datasetType: PlateDatasetType): string {
  return `${plateId}-${datasetType}`;
}

/*
 * Parse plate id from unique source identifier for plate details dataset.
 *
 * Expects an identifier of the shape generated by getPlateSourceIdentifier and must be kept in sync.
 */
export function parsePlateSourceIdentifier(identifier: string): string {
  const sourceIdParts = identifier.split('-');
  assert(sourceIdParts.length === 2, 'Expected 2 parts of the plate dataset identifier');
  return sourceIdParts[0];
}

/*
 * Generate a unique source identifier for a plate record dataset.
 *
 * Must be kept in sync with parsePlateRecordSourceIdentifier.
 */
const PLATE_RECORD_NAME_ESCAPE_CHARACTER = '%';
export function getPlateRecordSourceIdentifier(
  plateRecord: PlateRecordSpec,
  datasetType: PlateDatasetType
): string {
  const escapedName = plateRecord.name.replace(/-/g, `${PLATE_RECORD_NAME_ESCAPE_CHARACTER}-`);
  return `${plateRecord.id}-${plateRecord.plateId}-${escapedName}-${datasetType}`;
}

/*
 * Parse plate record & plate id from unique source identifier for dataset.
 *
 * Expects an identifier of the shape generated by getPlateRecordSourceIdentifier and must be kept in sync.
 */
export function parsePlateRecordSourceIdentifier(identifier: string): PlateRecordSpec {
  // This regex splits only on hyphens that are _not_ immediately preceded by our escape character, so it
  // will not split on any hyphens in the name. This uses a negative look behind group (?<!...)
  const sourceIdParts = identifier.split(new RegExp(`(?<!${PLATE_RECORD_NAME_ESCAPE_CHARACTER})-`));
  assert(sourceIdParts.length === 4, 'Expected 4 parts of the plate record dataset identifier');
  const unescapedName = sourceIdParts[2].replace(
    new RegExp(`${PLATE_RECORD_NAME_ESCAPE_CHARACTER}-`, 'g'),
    '-'
  );
  return {id: sourceIdParts[0], plateId: sourceIdParts[1], name: unescapedName};
}

/*
 * Generate a unique source identifier for a plate design dataset.
 *
 * Must be kept in sync with parsePlateDesignSourceIdentifier.
 */
export function getPlateDesignSourceIdentifier(
  plateDesign: PlateDesignSpec,
  datasetType: PlateDatasetType
): string {
  return `${plateDesign.id}-${plateDesign.plateDesignVersionId}-${datasetType}`;
}

/*
 * Parse plate design & version id from unique source identifier for dataset.
 *
 * Expects an identifier of the shape generated by getPlateDesignSourceIdentifier and must be kept in sync.
 */
export function parsePlateDesignSourceIdentifier(identifier: string): PlateDesignSpec {
  const sourceIdParts = identifier.split('-');
  assert(sourceIdParts.length === 3, 'Expected 3 parts of the plate design dataset identifier');
  return {id: sourceIdParts[0], plateDesignVersionId: sourceIdParts[1]};
}
